import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, Calendar, CheckCircle2, Inbox, Target, TrendingUp } from "lucide-react";

const statusOrder = ["new", "contacted", "qualified", "proposal", "won", "lost"];

const statusLabel = {
  new: "Noi",
  contacted: "Contactate",
  qualified: "Calificate",
  proposal: "Ofertă / negociere",
  won: "Câștigate",
  lost: "Pierdute",
};

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await fetch("/api/admin/leads-stats.php", { credentials: "include", cache: "no-store" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setError(j.detail || "Nu am putut încărca dashboard-ul.");
        return;
      }
      setData(j);
    } catch {
      setError("Eroare de rețea.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const summary = data?.summary;

  const cards = useMemo(
    () => [
      { key: "total", label: "Total lead-uri", value: summary?.total ?? 0, icon: Inbox },
      { key: "today", label: "Noi azi", value: summary?.today ?? 0, icon: Calendar },
      { key: "week", label: "Ultimele 7 zile", value: summary?.last_7_days ?? 0, icon: TrendingUp },
      { key: "month", label: "Ultimele 30 zile", value: summary?.last_30_days ?? 0, icon: BarChart3 },
    ],
    [summary]
  );

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <section className="rounded-[1.8rem] bg-[#3e2723] p-7 text-[#fbd9e5] shadow-[0_16px_40px_rgba(62,39,35,0.35)]">
        <p className="text-[11px] uppercase tracking-[0.28em] text-[#fbd9e5]/65">Admin Dashboard</p>
        <h1 className="mt-3 text-3xl font-black tracking-tight">Lead Management</h1>
        <p className="mt-2 max-w-2xl text-sm text-[#fbd9e5]/78">
          Vezi rapid performanța formularelor, statusul lead-urilor și intră în inbox pentru follow-up.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={load}
            className="rounded-full bg-[#fbd9e5] px-4 py-2 text-xs font-semibold text-[#3e2723] hover:bg-white"
          >
            Reîmprospătează datele
          </button>
          <Link
            to="/admin/leads"
            className="rounded-full border border-[#fbd9e5]/35 px-4 py-2 text-xs font-semibold text-[#fbd9e5] hover:bg-[#fbd9e5]/10"
          >
            Deschide inbox lead-uri
          </Link>
        </div>
      </section>

      {error ? <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <article key={c.key} className="rounded-2xl border border-[#3e2723]/10 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-[#3e2723]/55">{c.label}</p>
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#fbd9e5]/50 text-[#3e2723]">
                  <Icon className="h-4 w-4" />
                </span>
              </div>
              <p className="mt-2 text-3xl font-bold tracking-tight text-[#3e2723]">{loading ? "…" : c.value}</p>
            </article>
          );
        })}
      </section>

      <section className="grid gap-4 lg:grid-cols-5">
        <article className="rounded-2xl border border-[#3e2723]/10 bg-white p-4 shadow-sm lg:col-span-2">
          <h2 className="text-sm font-semibold text-[#3e2723]">Pipeline status</h2>
          <div className="mt-3 space-y-2">
            {statusOrder.map((s) => {
              const val = summary?.status_counts?.[s] ?? 0;
              return (
                <div key={s} className="flex items-center justify-between rounded-lg bg-[#faf8f8] px-3 py-2 text-sm">
                  <span className="text-[#3e2723]/80">{statusLabel[s]}</span>
                  <span className="font-semibold text-[#3e2723]">{loading ? "…" : val}</span>
                </div>
              );
            })}
          </div>
        </article>

        <article className="rounded-2xl border border-[#3e2723]/10 bg-white p-4 shadow-sm lg:col-span-3">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[#3e2723]">Ultimele lead-uri</h2>
            <Link to="/admin/leads" className="text-xs font-semibold text-[#3e2723]/70 hover:text-[#3e2723]">
              Vezi toate
            </Link>
          </div>
          <div className="space-y-2">
            {(data?.recent || []).map((lead) => (
              <div key={lead.id} className="rounded-lg bg-[#faf8f8] px-3 py-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate font-medium text-[#3e2723]">{lead.name}</p>
                  <span className="rounded-full bg-[#fbd9e5] px-2 py-0.5 text-[10px] font-semibold uppercase text-[#3e2723]">
                    {statusLabel[lead.status] || lead.status}
                  </span>
                </div>
                <p className="truncate text-xs text-[#3e2723]/65">{lead.email}</p>
              </div>
            ))}
            {!loading && (!data?.recent || data.recent.length === 0) ? (
              <p className="rounded-lg bg-[#faf8f8] px-3 py-2 text-sm text-[#3e2723]/60">Nu există lead-uri încă.</p>
            ) : null}
          </div>
        </article>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-[#3e2723]/10 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-[#3e2723]">Quick action</h3>
          <p className="mt-1 text-xs text-[#3e2723]/60">Deschide inbox și procesează lead-urile noi.</p>
          <Link to="/admin/leads?status=new" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#3e2723]">
            <Target className="h-3.5 w-3.5" /> Vezi lead-uri noi
          </Link>
        </div>
        <div className="rounded-2xl border border-[#3e2723]/10 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-[#3e2723]">Best practice</h3>
          <p className="mt-1 text-xs text-[#3e2723]/60">Mută rapid lead-urile în `contacted` după primul răspuns.</p>
          <p className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> Workflow recomandat
          </p>
        </div>
        <div className="rounded-2xl border border-[#3e2723]/10 bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-[#3e2723]">Content sync</h3>
          <p className="mt-1 text-xs text-[#3e2723]/60">Texte și imagini rămân în tab-ul Personalizare / JSON expert.</p>
          <Link to="/admin/content" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#3e2723]">Mergi la personalizare</Link>
        </div>
      </section>
    </div>
  );
}
